"use client";

import { useSearchParams } from "next/navigation";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
}

export const CustomPagination = ({ currentPage, totalPages }: PaginationProps) => {
  const searchParams = useSearchParams();

  const generateHref = (page: number) => {
    const params = new URLSearchParams(searchParams?.toString() || "");
    params.set("page", page.toString());
    return `?${params.toString()}`;
  };

  const renderPageNumbers = () => {
    const pages = [];

    pages.push(
      <PaginationItem key={1}>
        <PaginationLink href={generateHref(1)} isActive={currentPage === 1}>
          1
        </PaginationLink>
      </PaginationItem>
    );

    if (currentPage > 3) {
      pages.push(
        <PaginationItem key="ellipsis1">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink href={generateHref(i)} isActive={i === currentPage}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (currentPage < totalPages - 3) {
      pages.push(
        <PaginationItem key="ellipsis2" className="max-md:hidden">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    if (totalPages > 1) {
      pages.push(
        <PaginationItem key={totalPages} className="max-md:hidden">
          <PaginationLink
            href={generateHref(totalPages)}
            isActive={currentPage === totalPages}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  return (
    <>
      {totalPages > 1 && (
        <Pagination className="my-8 flex justify-center">
          <PaginationContent>
            <PaginationItem>
              {currentPage > 1 && (
                <PaginationPrevious href={generateHref(currentPage - 1)} />
              )}
            </PaginationItem>
            {renderPageNumbers()}
            <PaginationItem>
              {currentPage < totalPages && (
                <PaginationNext href={generateHref(currentPage + 1)} />
              )}
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};
